import DRIVER_CSS from 'driver.js/dist/driver.css?inline';
import { COLORS, MOTION_DURATIONS, MOTION_STEPS, TOKENS_CSS } from './tokens.js';
import { GLOBAL_BASE_CSS, SURFACE_CSS } from './surfaces.css.js';
import { pixelIconDataUri } from '../PixelIcon.js';

/** The <select> arrow: a native control cannot host an <svg> child. */
const SELECT_ARROW = pixelIconDataUri('chevronDown', COLORS.textOnDark);

/**
 * Coin i of three hops during its third of the loader cycle — up and down in
 * stepped frames — and rests the rest of the time. The reduced-motion pair keeps
 * the turn order as a dim/lit blink with no movement.
 */
function loaderCoinKeyframes(): string {
  return [1, 2, 3].map((coin) => {
    const from = Math.round((coin - 1) / 3 * 1000) / 10;
    const peak = Math.round(((coin - 1) / 3 + 1 / 6) * 1000) / 10;
    const to = Math.round(coin / 3 * 1000) / 10;
    return `@keyframes cards-loader-coin-${coin} {
  0%, ${from}% { transform: translateY(0); animation-timing-function: ${MOTION_STEPS.bounce}; }
  ${peak}% { transform: translateY(calc(var(--space-3) * -1)); animation-timing-function: ${MOTION_STEPS.bounce}; }
  ${to}%, 100% { transform: translateY(0); }
}
@keyframes cards-loader-coin-blink-${coin} {
  0% { opacity: ${from === 0 ? 1 : .35}; }
  ${from}% { opacity: 1; }
  ${to}%, 100% { opacity: .35; }
}`;
  }).join('\n');
}

export const KIT_CSS = `${TOKENS_CSS}
${GLOBAL_BASE_CSS}
${DRIVER_CSS}
/* Modal (design system 02 §2.5, 03 §3.4): a panel dropped onto a dark scrim. */
.cards-kit-modal-overlay {
  position: fixed;
  inset: 0;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  /* The bottom inset keeps a dialog and its step clear of the BETA strip. */
  padding: var(--space-4) var(--space-4) calc(var(--space-4) + var(--step-4) + var(--cards-beta-ticker-reserve));
  background: var(--c-scrim);
}
.cards-kit-modal-overlay[data-modal-layer="planning"] { z-index: 115; }
.cards-kit-modal-overlay[data-modal-layer="choice"] { z-index: 120; }
.cards-kit-modal-overlay[data-modal-layer="viewer"] { z-index: 140; }
.cards-kit-modal-overlay[data-modal-layer="detail"] { z-index: 150; }
.cards-kit-modal-overlay[data-modal-layer="confirm"] { z-index: 160; }
.cards-kit-modal-dialog { box-sizing: border-box; }
/* The magnified card-detail dialog is a card, not a panel: it keeps its own
   frame and its own enter (surfaces.css.ts) so the scale(1.5) is preserved. */
/* The panel (02 §1, 01 §5–7): an ink outline, the slate face and a solid ink
   step — on the night table as on a panel (K2: ink at 40 % vanished into the
   indigo). One recipe for every panel; a panel sets only what differs:
   --cards-panel-radius (radius-xl) and --cards-panel-step (step-3). :where keeps
   the recipe at zero specificity, so a panel's own rules and states still win.
   The driver.js popover keeps its own copy: the library's rule would beat it. */
:where(
  .cards-kit-modal-dialog:not(.cards-card-detail-dialog),
  .cards-kit-stat-tile,
  .onboarding-loading > div,
  .cards-ai-panel,
  .cards-target-picker,
  .cards-match-result-banner,
  .cards-phase-banner,
  .cards-deck-option,
  .cards-deck-seat-statuses,
  .cards-lobby-panel,
  .activity-admin-panel,
  .cards-replay-decks,
  .auth-account-panel,
  .cards-targeting-bar,
  .cards-kit-status-panel,
  .auth-blocking-panel,
  .lab-sandbox-bottombar,
  .deck-row,
  .deck-empty,
  .deck-status,
  .lab-empty,
  .deck-passive-summary
) {
  border: var(--stroke-l) solid var(--c-outline);
  border-radius: var(--cards-panel-radius, var(--radius-xl));
  background: var(--c-panel-bg);
  box-shadow: 0 var(--cards-panel-step, var(--step-3)) 0 var(--c-step-shadow);
}
.cards-kit-modal-dialog:not(.cards-card-detail-dialog) {
  max-width: 100%;
  max-height: 100%;
  --cards-panel-radius: var(--radius-2xl);
  --cards-panel-step: var(--step-4);
  padding: var(--space-6);
  color: var(--c-text-on-dark);
  font: var(--type-text-m);
}
.cards-kit-modal-dialog:not(.cards-card-detail-dialog) > :is(h1, h2, h3),
.cards-kit-modal-dialog .cards-kit-panel-header-title {
  margin: 0 0 var(--space-3);
  color: var(--c-text-on-dark);
  font: var(--type-display-s);
  text-transform: uppercase;
  text-shadow: none;
}
@media (max-width: 620px) {
  .cards-kit-modal-dialog:not(.cards-card-detail-dialog) > :is(h1, h2, h3),
  .cards-kit-modal-dialog .cards-kit-panel-header-title { font: var(--type-display-xs); }
}
.cards-kit-modal-dialog:not(.cards-card-detail-dialog) > p {
  color: var(--c-text-muted-on-dark);
  font: var(--type-text-m);
}
.cards-kit-modal-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: var(--space-3);
}
/* Backdrop fades; the panel drops in from above and settles past its mark. */
@keyframes cards-modal-overlay-in { from { opacity: 0; } }
@keyframes cards-modal-dialog-in {
  from { opacity: 0; transform: translateY(-16px); }
  to { opacity: 1; transform: translateY(0); }
}
.cards-kit-modal-overlay { animation: cards-modal-overlay-in var(--dur-quick) var(--ease-out); }
.cards-kit-modal-dialog:not(.cards-card-detail-dialog) {
  animation: cards-modal-dialog-in var(--dur-base) var(--ease-overshoot);
}
/* Closing is mirrored by Modal.tsx: the unmounting overlay is cloned into an
   inert [data-closing] ghost that plays these and removes itself. The whole
   ghost fades via the overlay; only non-detail dialogs add the sink-away
   transform (the magnified detail dialog keeps its scale(1.5) untouched). */
@keyframes cards-modal-overlay-out {
  from { opacity: var(--cards-modal-exit-overlay-opacity, 1); }
  to { opacity: 0; }
}
@keyframes cards-modal-dialog-out {
  from {
    opacity: var(--cards-modal-exit-dialog-opacity, 1);
    transform: var(--cards-modal-exit-dialog-transform, none);
  }
  to { opacity: 0; transform: translateY(8px); }
}
.cards-kit-modal-overlay[data-closing="true"] {
  animation: cards-modal-overlay-out var(--dur-quick) var(--ease-in) forwards;
  pointer-events: none;
}
.cards-kit-modal-overlay[data-closing="true"] .cards-kit-modal-dialog:not(.cards-card-detail-dialog) {
  animation: cards-modal-dialog-out var(--dur-quick) var(--ease-in) forwards;
}
@media (prefers-reduced-motion: reduce) {
  .cards-kit-modal-overlay,
  .cards-kit-modal-dialog:not(.cards-card-detail-dialog) { animation: none; }
}
body[data-cards-modal-open="true"] .cards-ai-launcher,
body[data-cards-modal-open="true"] .cards-ai-panel {
  visibility: hidden;
  pointer-events: none;
}
body[data-cards-modal-open="true"] .cards-ai-highlight {
  z-index: auto;
  outline: none !important;
  animation: none;
}
.cards-choice-overlay,
.cards-planning-target-overlay {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-4);
}
.cards-card-detail-overlay,
.cards-deck-viewer-overlay {
  display: grid;
  place-items: center;
  padding: var(--space-4);
}
/* Fields (02 §2.6): sunken into the panel — dark face, inner step, a slate rim
   that turns gold on focus and red when invalid. */
.cards-kit-text-field,
.cards-kit-select {
  width: 100%;
  min-height: 44px;
  box-sizing: border-box;
  border: var(--stroke-l) solid var(--c-panel-edge);
  border-radius: var(--radius-m);
  padding: 0 var(--space-3);
  background-color: var(--c-field-bg);
  box-shadow: inset 0 var(--step-2) 0 color-mix(in srgb, var(--c-step-shadow) 35%, transparent);
  color: var(--c-text-on-dark);
  font: var(--type-text-m);
  outline: none;
}
.cards-kit-text-field::placeholder { color: var(--c-text-muted-on-dark); opacity: 1; }
@media (pointer: coarse) {
  .cards-kit-text-field,
  .cards-kit-select,
  .cards-kit-rich-text-editor { font: var(--type-text-input); }
}
.cards-kit-select {
  appearance: none;
  padding-right: calc(var(--space-3) * 2 + 14px);
  background-image: ${SELECT_ARROW};
  background-repeat: no-repeat;
  background-position: right var(--space-3) center;
  background-size: 14px 14px;
  cursor: pointer;
}
.cards-kit-text-field:focus,
.cards-kit-select:focus {
  border-color: var(--c-selection);
}
.cards-kit-text-field[data-invalid="true"],
.cards-kit-select[data-invalid="true"] {
  border-color: var(--c-negative);
}
.cards-kit-text-field:disabled,
.cards-kit-select:disabled {
  border-color: var(--c-panel-raised);
  background-color: var(--c-panel-raised);
  box-shadow: none;
  color: var(--c-text-disabled);
  cursor: not-allowed;
}
.cards-kit-rich-text {
  overflow: hidden;
  border: var(--stroke-l) solid var(--c-panel-edge);
  border-radius: var(--radius-m);
  background: var(--c-field-bg);
  box-shadow: inset 0 var(--step-2) 0 color-mix(in srgb, var(--c-step-shadow) 35%, transparent);
}
.cards-kit-rich-text:focus-within { border-color: var(--c-selection); }
.cards-kit-rich-text[data-invalid="true"] { border-color: var(--c-negative); }
.cards-kit-rich-text-toolbar {
  display: flex;
  gap: var(--space-1);
  padding: var(--space-1);
  border-bottom: var(--stroke-s) solid var(--c-panel-edge);
  background: var(--c-panel-bg);
}
.cards-kit-rich-text-toolbar .cards-kit-button {
  min-width: 32px;
  padding-inline: var(--space-2);
}
.cards-kit-rich-text-toolbar .cards-pixel-icon { width: 14px; height: 14px; }
/* Inside the field's own gold rim the toolbar ring is drawn inward. */
.cards-kit-rich-text-toolbar :focus-visible { outline-offset: calc(var(--stroke-l) * -1); }
.cards-kit-rich-text-editor {
  min-height: 132px;
  max-height: 280px;
  overflow-y: auto;
  box-sizing: border-box;
  padding: var(--space-3);
  color: var(--c-text-on-dark);
  font: var(--type-text-m);
  outline: none;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
.cards-kit-rich-text-editor[data-empty="true"]::before {
  color: var(--c-text-muted-on-dark);
  content: attr(data-placeholder);
  pointer-events: none;
}
.cards-kit-rich-text-editor ul,
.cards-kit-rich-text-editor ol { margin: 0; padding-left: 22px; }
/* Alert (02 §2.4): a panel rimmed in its tone with the tone's pixel icon. */
.cards-kit-alert {
  position: relative;
  box-sizing: border-box;
  border: var(--stroke-l) solid var(--cards-alert-tone);
  border-radius: var(--radius-xl);
  padding: var(--space-3) var(--space-4) var(--space-3) calc(var(--space-4) + 21px + var(--space-3));
  background: var(--c-panel-bg);
  box-shadow: 0 var(--step-3) 0 var(--c-step-shadow);
  color: var(--c-text-on-dark);
  font: var(--type-text-m);
}
.cards-kit-alert-icon {
  position: absolute;
  top: var(--space-3);
  left: var(--space-4);
  width: 21px;
  height: 21px;
  color: var(--cards-alert-tone);
}
.cards-kit-alert--error { --cards-alert-tone: var(--c-negative); color: var(--c-text-negative-on-dark); }
.cards-kit-alert--warning { --cards-alert-tone: var(--c-selection); }
.cards-kit-alert--info { --cards-alert-tone: var(--c-freeze); }
.cards-kit-alert--success { --cards-alert-tone: var(--c-positive); }
/* Badge → ярлык (02 §2.2): a flat filled tag with a dark rim and a 2px step.
   Short labels (Badge marks them --display) are set in the pixel face. */
.cards-kit-badge {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  width: fit-content;
  max-width: 100%;
  box-sizing: border-box;
  border: var(--stroke-m) solid var(--c-outline);
  border-radius: var(--radius-xs);
  padding: var(--space-1) var(--space-2);
  box-shadow: 0 var(--step-1) 0 var(--c-step-shadow);
  font: var(--type-text-xs);
  letter-spacing: .06em;
  line-height: 1;
  text-transform: uppercase;
  white-space: nowrap;
}
.cards-kit-badge--display { font: var(--type-display-2xs); letter-spacing: 0; line-height: 1; }
.cards-kit-badge--neutral { background: var(--c-panel-raised); color: var(--c-text-on-dark); }
.cards-kit-badge--success { background: var(--c-positive); color: var(--c-text-on-card); }
.cards-kit-badge--warning { background: var(--c-selection); color: var(--c-text-on-energy); }
.cards-kit-badge--danger {
  background: var(--c-negative);
  color: var(--c-text-on-color);
  text-shadow: var(--text-outline-1);
}
.cards-kit-badge--info { background: var(--c-freeze); color: var(--c-text-on-card); }
/* StatTile → счётчик (02 §2.3): only where the number is the point. */
.cards-kit-stat-tile {
  box-sizing: border-box;
  padding: var(--space-3) var(--space-4);
}
.cards-kit-stat-tile-value {
  display: block;
  margin-bottom: var(--space-1);
  color: var(--c-text-on-dark);
  font: var(--type-display-m);
  font-variant-numeric: tabular-nums;
}
.cards-kit-stat-tile--success .cards-kit-stat-tile-value { color: var(--c-positive); }
.cards-kit-stat-tile--warning .cards-kit-stat-tile-value { color: var(--c-selection); }
.cards-kit-stat-tile--danger .cards-kit-stat-tile-value { color: var(--c-text-negative-on-dark); }
.cards-kit-stat-tile-label { color: var(--c-text-muted-on-dark); font: var(--type-text-s); }
/* PanelHeader (02 §2.7): pixel title by heading level, gold section kicker. */
.cards-kit-panel-header {
  min-width: 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-4);
}
.cards-kit-panel-header-copy { min-width: 0; }
.cards-kit-panel-header-kicker {
  color: var(--c-selection);
  font: var(--type-display-2xs);
  letter-spacing: 0;
  text-transform: uppercase;
}
/* Press Start 2P is very wide, so only the page title (h1) is large; section
   titles sit inside panels and narrow columns, one step down on phones. */
.cards-kit-panel-header-title {
  margin: var(--space-2) 0;
  color: var(--c-text-on-dark);
  font: var(--type-display-xs);
  text-transform: uppercase;
  text-shadow: var(--text-outline-2);
  overflow-wrap: break-word;
}
h1.cards-kit-panel-header-title { font: var(--type-display-l); }
h2.cards-kit-panel-header-title { font: var(--type-display-s); }
@media (max-width: 620px) {
  .cards-kit-panel-header-title { font: var(--type-display-2xs); }
  h1.cards-kit-panel-header-title { font: var(--type-display-m); }
  h2.cards-kit-panel-header-title { font: var(--type-display-xs); }
}
.cards-kit-panel-header-description {
  margin: 0;
  color: var(--c-text-muted-on-table);
  font: var(--type-text-m);
}
.cards-kit-panel-header-actions { flex: none; }
.cards-deck-selection-shell {
  width: min(1180px, 100%);
  margin: 0 auto;
}
.cards-deck-selection-header {
  display: block;
  margin-bottom: 24px;
}
.cards-deck-selection-error { margin-bottom: var(--space-5); }
.cards-deck-viewer-header .cards-kit-panel-header-title { margin: 0; }
.cards-deck-viewer-header .cards-kit-panel-header-description { margin-top: var(--space-1); }
.cards-deck-viewer-count { min-width: 28px; justify-content: center; }
.cards-deck-viewer-card .cards-catalog-card-footer { display: flex; justify-content: center; }
/* The large card L (design system 02 §3.1–3.2): the catalog card, the hover
   card and the card dialog. A face by type in an ink outline with a step; a
   gold coin and a type tag over the corners of a large art window, the name,
   the rules text, then the combat chips along the bottom. A surface that is not
   a card (a deck passive, the cell state) has no type and stays a slate panel. */
.cards-card-detail-panel {
  --cards-card-rim-width: var(--stroke-l);
  position: relative;
  isolation: isolate;
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;
  border: var(--stroke-l) solid var(--cards-card-edge);
  border-radius: var(--radius-xl);
  background: var(--cards-card-face);
  color: var(--cards-card-ink);
  /* A solid step instead of the old blurred drop: the blur painted over the
     footer under the card and dimmed its buttons (no blurred shadows, 01 §7). */
  box-shadow: var(--cards-card-bevel, 0 0 transparent), 0 var(--step-4) 0 var(--cards-card-step-color, var(--c-step-shadow)), var(--cards-card-rim);
}
/* Its colours come from the card skin (surfaces.css.ts). A panel that is not a
   unit, a structure or a spell is not a card face — a deck passive, the cell
   state, a location effect (EFFECT) — and stays slate (02 §3.2). */
.cards-card-detail-panel:not([data-card-type="UNIT"], [data-card-type="STRUCTURE"], [data-card-type="SPELL"]) {
  --cards-card-face: var(--c-panel-bg);
  --cards-card-ink: var(--c-text-on-dark);
  --cards-card-muted: var(--c-text-muted-on-dark);
}
.cards-card-detail-compact { padding: var(--space-2); }
.cards-card-detail-comfortable { padding: var(--space-4); }
.cards-card-detail-header {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: var(--space-2);
}
/* The art window (01 §10): a light window the width of the card. */
.cards-card-detail-header > :is(.cards-card-glyph, .cards-deck-passive-glyph, svg) {
  width: 100%;
  height: calc(120px + 2 * var(--space-1));
  box-sizing: border-box;
  padding: var(--space-1);
  border-radius: var(--radius-s);
  background: var(--c-card-art-window);
  box-shadow: var(--cards-card-window-inset);
}
/* The cost coin (01 §5–7, 02 §3.1): one recipe at every size — gold, an ink
   outline, a solid ink step, an ink number. Two tiers: on every card but L a
   stroke-m outline and step-1; on L (large) stroke-l and step-2, set through
   --cards-coin-stroke/-step. A coin sets only its size, font and place; a state
   swaps --cards-coin-face/-ink. The L coin, the hand coin and the MiniCard coin. */
:is(.cards-card-detail-coin, .cards-hand-cost, .cards-mini-card-energy) {
  display: grid;
  place-items: center;
  box-sizing: border-box;
  border: var(--cards-coin-stroke, var(--stroke-m)) solid var(--c-outline);
  border-radius: var(--radius-round);
  background: var(--cards-coin-face, var(--c-energy));
  box-shadow: 0 var(--cards-coin-step, var(--step-1)) 0 var(--c-step-shadow);
  color: var(--cards-coin-ink, var(--c-text-on-energy));
  line-height: 1;
  white-space: nowrap;
}
.cards-card-detail-coin {
  --cards-coin-stroke: var(--stroke-l);
  --cards-coin-step: var(--step-2);
  position: absolute;
  z-index: 2;
  top: var(--space-1);
  left: var(--space-1);
  width: 36px;
  height: 36px;
  font: var(--type-display-s);
  line-height: 1;
}
.cards-card-detail-heading { min-width: 0; display: grid; gap: var(--space-1); }
/* The type tag sits over the art window's top-right corner. */
.cards-card-detail-meta {
  position: absolute;
  z-index: 2;
  top: var(--space-1);
  right: var(--space-1);
  padding: 3px var(--space-1);
  border: var(--stroke-m) solid var(--c-outline);
  border-radius: var(--radius-xs);
  background: var(--c-panel-raised);
  color: var(--c-text-on-dark);
  font: var(--type-display-3xs);
  line-height: 1;
  text-transform: uppercase;
}
/* The cost in words stays for assistive technology; the coin shows it. */
.cards-card-detail-meta-resource {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}
/* A surface without art (the cell state) keeps its meta in the flow. */
.cards-card-detail-header:not(:has(> svg)) .cards-card-detail-meta { position: static; justify-self: start; }
.cards-card-detail-title {
  position: relative;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  font: var(--type-text-l);
  font-weight: 600;
  line-height: 1.2;
}
.cards-card-detail-stats {
  position: relative;
  display: flex;
  justify-content: space-between;
  gap: var(--space-2);
  margin-top: var(--space-2);
}
/* In the card's own column the chips go to the bottom edge (02 §3.2). */
.cards-catalog-card > .cards-card-detail-panel > .cards-card-detail-stats { order: 2; }
/* Combat chips (02 §3.1, 01 §5–7): one recipe in every card — attack red on
   the left, health blue on the right, a cream number in a dark pixel outline
   (01 §2.4 — the outline is what makes white readable on red and blue), an ink
   outline and a solid ink step. Two tiers: on every card but L stroke-m,
   radius-xs and step-1; on L (large) stroke-l, radius-s and step-2, set
   through --cards-chip-stroke/-radius/-step. A mark on the chip (a wound) is
   --cards-chip-mark, drawn over it without losing the step. A chip context sets
   only its size, font and layout. :where keeps the recipe at the specificity of
   the context's own selector, so every override a context already had still
   wins. The L chips, the hand/board/plan chips and the MiniCard chips. */
:where(.cards-card-detail-stats) > span,
:where(.cards-hand-stats, .cards-board-card-stats, .cards-staged-card-stats) .cards-card-stat,
:where(.cards-mini-card-stats) > span[aria-label] {
  --cards-chip-face: var(--c-attack);
  box-sizing: border-box;
  border: var(--cards-chip-stroke, var(--stroke-m)) solid var(--c-outline);
  border-radius: var(--cards-chip-radius, var(--radius-xs));
  background: var(--cards-chip-face);
  box-shadow: var(--cards-chip-mark, 0 0 transparent), 0 var(--cards-chip-step, var(--step-1)) 0 var(--c-step-shadow);
  color: var(--c-text-on-color);
  text-shadow: var(--text-outline-1);
}
:where(.cards-card-detail-stats) > span:last-child,
:where(.cards-hand-stats, .cards-board-card-stats, .cards-staged-card-stats) .cards-card-stat:last-child,
:where(.cards-mini-card-stats) > span[aria-label]:last-child { --cards-chip-face: var(--c-health); }
.cards-card-detail-stats > span {
  --cards-chip-stroke: var(--stroke-l);
  --cards-chip-radius: var(--radius-s);
  --cards-chip-step: var(--step-2);
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  min-width: 52px;
  height: 30px;
  justify-content: center;
  padding: 0 var(--space-2);
  font: var(--type-display-xs);
  line-height: 1;
}
.cards-card-detail-stats .cards-pixel-icon { width: 14px; height: 14px; filter: drop-shadow(0 1px 0 var(--c-outline)); }
.cards-card-detail-description {
  position: relative;
  margin: var(--space-2) 0;
  color: var(--cards-card-ink);
  font: var(--type-text-s);
}
.cards-card-detail-section { display: grid; gap: var(--space-1); }
.cards-card-detail-statuses { margin-bottom: var(--space-3); }
.cards-card-detail-section-label {
  color: var(--cards-card-muted);
  font: var(--type-display-3xs);
  text-transform: uppercase;
}
/* A status row: its family's colour on the left edge (04 §2.5), the text in
   the face's own ink, the row a faint wash of that ink — it reads on every face. */
.cards-card-detail-status,
.cards-card-detail-keyword {
  border-radius: var(--radius-xs);
  background: color-mix(in srgb, var(--cards-card-ink) 8%, transparent);
}
.cards-card-detail-status {
  padding: var(--space-1) var(--space-2);
  border-left: var(--stroke-l) solid var(--cards-card-status-color);
}
.cards-card-detail-status strong,
.cards-card-detail-keyword-static strong {
  display: block;
  font: var(--type-text-xs);
}
.cards-card-detail-status span,
.cards-card-detail-keyword-static span {
  display: block;
  margin-top: 2px;
  color: var(--cards-card-muted);
  font: var(--type-text-xs);
  font-weight: 500;
}
/* Keyword help: a neutral tag that opens a sunken panel under it (04 §2.6). */
.cards-card-detail-keywords-collapsed { gap: var(--space-1); }
.cards-card-detail-keyword { padding: 0; background: transparent; }
.cards-card-detail-keyword-static { padding: var(--space-1) var(--space-2); background: color-mix(in srgb, var(--cards-card-ink) 8%, transparent); }
.cards-card-detail-keyword summary {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: 2px var(--space-2);
  border: var(--stroke-m) solid var(--c-outline);
  border-radius: var(--radius-xs);
  background: var(--c-panel-edge);
  color: var(--c-text-on-dark);
  cursor: pointer;
  font: var(--type-text-xs);
  list-style: none;
}
.cards-card-detail-keyword summary::-webkit-details-marker { display: none; }
.cards-card-detail-keyword summary::before {
  content: '';
  width: 7px;
  height: 7px;
  background: ${SELECT_ARROW} center / contain no-repeat;
  transform: rotate(-90deg);
}
.cards-card-detail-keyword[open] summary::before { transform: none; }
.cards-card-detail-keyword p {
  margin: var(--space-1) 0 0;
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-xs);
  background: var(--c-field-bg);
  color: var(--c-text-on-dark);
  font: var(--type-text-xs);
  font-weight: 500;
}
.cards-card-detail-keyword-static strong { color: var(--cards-card-ink); }
/* Deck-passive decorative background: a themed tile repeated behind the card
   content at low opacity, clipped to the card and kept below the text. */
.cards-card-detail-pattern {
  position: absolute;
  inset: 0;
  z-index: 0;
  opacity: .15;
  pointer-events: none;
}
.cards-card-detail-panel > *:not(.cards-card-detail-pattern):not(.cards-tilt-glare) { position: relative; z-index: 1; }
/* Balatro-style pointer tilt + glare. On a fine hover pointer it follows the
   cursor; on touch it starts on finger-press and follows the finger while it
   slides (see useCardTilt), so phones get the same "hover" feedback. A press
   that lands on a part the card scrolls itself is left flat — the scroll wins.
   reduced-motion flattens it. The transform/shine react to CSS vars written by
   useCardTilt from the pointer position.

   At rest there is NO transform, so the pixel art stays crisp — a lingering
   perspective() forces a blurry composited raster. The 3D tilt only engages
   while data-tilting is set, i.e. the pointer is moving over (or pressing) the
   card. */
.cards-tilt {
  transform-style: preserve-3d;
  transition: transform var(--dur-move) var(--ease-overshoot);
  /* Panning stays with the browser: the touch tilt never preventDefaults, so a
     gesture that turns into a scroll is handed over (and cancels the tilt). */
  touch-action: manipulation;
  -webkit-user-select: none;
  user-select: none;
  -webkit-touch-callout: none;
}
.cards-tilt[data-tilting="true"] {
  transform: perspective(720px) rotateX(var(--cards-tilt-rx, 0deg)) rotateY(var(--cards-tilt-ry, 0deg));
  transition: transform var(--dur-tap) linear;
}
.cards-tilt[data-tilt-input="tap"] { transition: transform var(--dur-quick) var(--ease-out); }
/* A single diagonal sheen made of three hard-stepped shades (no smooth
   blend), to match the pixel-art look. Shared by .cards-tilt-glare and the
   hand-card ::after glare (surfaces.css.ts) so every card glints the same. */
:root {
  --cards-sheen: linear-gradient(
    110deg,
    transparent 45%,
    color-mix(in srgb, var(--c-glare) 4%, transparent) 45%,
    color-mix(in srgb, var(--c-glare) 4%, transparent) 47%,
    color-mix(in srgb, var(--c-glare) 10%, transparent) 47%,
    color-mix(in srgb, var(--c-glare) 10%, transparent) 49%,
    color-mix(in srgb, var(--c-glare) 18%, transparent) 49%,
    color-mix(in srgb, var(--c-glare) 18%, transparent) 51%,
    color-mix(in srgb, var(--c-glare) 10%, transparent) 51%,
    color-mix(in srgb, var(--c-glare) 10%, transparent) 53%,
    color-mix(in srgb, var(--c-glare) 4%, transparent) 53%,
    color-mix(in srgb, var(--c-glare) 4%, transparent) 55%,
    transparent 55%
  );
}
/* The glare (03 §7): ONE stepped sheen in every card — the tilt glare of the
   board, catalog and mini cards and the ::after of the hand and plan cards.
   The sheen is the mask, not the paint: it travels with the pointer through
   mask-position, which may animate, where background-position may not. Each
   glare sets only its layer, opacity and timing, and --cards-sheen-at — where
   the pointer is, from whichever tilt drives that card. */
.cards-tilt-glare,
.cards-hand-card::after,
.cards-staged-card::after,
.cards-touch-drag-preview::after {
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  background: var(--c-glare);
  -webkit-mask-image: var(--cards-sheen);
  mask-image: var(--cards-sheen);
  -webkit-mask-size: 260% 260%;
  mask-size: 260% 260%;
  -webkit-mask-position: var(--cards-sheen-at);
  mask-position: var(--cards-sheen-at);
  mix-blend-mode: screen;
}
.cards-tilt-glare {
  --cards-sheen-at: var(--cards-glare-x, 50%) var(--cards-glare-y, 50%);
  z-index: 4;
  opacity: var(--cards-tilt-glow, 0);
  transition:
    opacity var(--dur-move) var(--ease-out),
    -webkit-mask-position var(--dur-base) var(--ease-out),
    mask-position var(--dur-base) var(--ease-out);
}
.cards-tilt[data-tilt-input="tap"] > .cards-tilt-glare {
  transition-duration: var(--dur-base);
}
/* Both selectors: a bare .cards-tilt lost to .cards-tilt[data-tilting] and the
   card still tilted under reduced motion (03 §6). */
@media (prefers-reduced-motion: reduce) {
  .cards-tilt,
  .cards-tilt[data-tilting="true"] { transform: none; }
  .cards-tilt-glare { display: none; }
}
.cards-delayed-loader {
  position: fixed;
  z-index: 200;
  inset: 0;
  display: grid;
  place-items: center;
  box-sizing: border-box;
  padding: var(--space-6);
  background: var(--cards-felt);
}
/* Three gold coins hop in turn, in stepped frames (02 §2.8, 03 §2.3). The
   wall-clock phase DelayedLoader pins on the row is inherited by every coin. */
.cards-delayed-loader-mark {
  display: flex;
  align-items: flex-end;
  gap: var(--space-2);
  height: calc(18px + var(--space-3));
}
.cards-delayed-loader-mark > span {
  width: 18px;
  height: 18px;
  box-sizing: border-box;
  border: var(--stroke-m) solid var(--c-outline);
  border-radius: var(--radius-round);
  background: var(--c-energy);
  box-shadow: 0 var(--step-1) 0 var(--c-step-shadow);
  animation: cards-loader-coin-1 ${MOTION_DURATIONS.loader}ms linear infinite;
  animation-delay: inherit;
}
.cards-delayed-loader-mark > span:nth-child(2) { animation-name: cards-loader-coin-2; }
.cards-delayed-loader-mark > span:nth-child(3) { animation-name: cards-loader-coin-3; }
${loaderCoinKeyframes()}
@media (prefers-reduced-motion: reduce) {
  /* No hop: the coins stand still and light up in turn instead. These beat the
     global reduced-motion clamp, which would otherwise freeze the cue. */
  .cards-delayed-loader-mark > span {
    animation: cards-loader-coin-blink-1 ${MOTION_DURATIONS.loader}ms ${MOTION_STEPS.flash} infinite !important;
    animation-delay: inherit !important;
  }
  .cards-delayed-loader-mark > span:nth-child(2) { animation-name: cards-loader-coin-blink-2 !important; }
  .cards-delayed-loader-mark > span:nth-child(3) { animation-name: cards-loader-coin-blink-3 !important; }
}
.onboarding-loading {
  min-height: 100%;
  display: grid;
  place-items: center;
  box-sizing: border-box;
  padding: var(--space-6);
  background: var(--cards-felt);
  color: var(--c-text-on-dark);
  font: var(--type-text-m);
}
.onboarding-loading > div {
  display: grid;
  justify-items: start;
  gap: var(--space-4);
  width: min(460px, 100%);
  box-sizing: border-box;
  padding: var(--space-6);
  --cards-panel-step: var(--step-4);
}
/* Onboarding popovers (driver.js) are panels with a dark pixel arrow; «Далее»
   is the primary plaque, the rest are secondary (05 §11). */
.cards-onboarding-popover.driver-popover {
  --driver-popover-font-family: var(--font-text);
  min-width: min(300px, calc(100vw - 24px));
  max-width: min(380px, calc(100vw - 24px));
  border: var(--stroke-l) solid var(--c-outline);
  border-radius: var(--radius-xl);
  padding: var(--space-4);
  background: var(--c-panel-bg);
  color: var(--c-text-on-dark);
  box-shadow: 0 var(--step-4) 0 var(--c-step-shadow);
}
.cards-onboarding-popover .driver-popover-title {
  color: var(--c-text-on-dark);
  font: var(--type-display-xs);
  text-transform: uppercase;
}
.cards-onboarding-popover .driver-popover-description {
  color: var(--c-text-muted-on-dark);
  font: var(--type-text-s);
}
.cards-onboarding-popover .driver-popover-progress-text {
  color: var(--c-text-muted-on-dark);
  font: var(--type-display-3xs);
}
.cards-onboarding-popover .driver-popover-footer-btn {
  min-height: 32px;
  border: var(--stroke-l) solid var(--c-outline);
  border-radius: var(--radius-m);
  padding: 0 var(--space-3);
  background: var(--c-panel-raised);
  box-shadow: 0 var(--step-2) 0 var(--c-step-shadow);
  color: var(--c-text-on-dark);
  font: var(--type-text-s);
  font-weight: 600;
  text-shadow: none;
}
.cards-onboarding-popover .driver-popover-footer-btn:active { box-shadow: none; transform: translateY(var(--step-2)); }
.cards-onboarding-popover .driver-popover-close-btn { color: var(--c-text-muted-on-dark); }
.cards-onboarding-popover .driver-popover-close-btn:hover,
.cards-onboarding-popover .driver-popover-close-btn:focus-visible { color: var(--c-selection); }
.cards-kit-modal-overlay.onboarding-skip-confirm-overlay { z-index: 1000000001; }
body.driver-active .cards-kit-modal-overlay.onboarding-skip-confirm-overlay,
body.driver-active .cards-kit-modal-overlay.onboarding-skip-confirm-overlay * {
  pointer-events: auto;
}
.onboarding-skip-confirm { width: min(360px, calc(100vw - 32px)); }
.onboarding-skip-confirm p { margin: 0; }
.onboarding-skip-confirm-actions { margin-top: var(--space-5); }
.cards-onboarding-popover .driver-popover-next-btn {
  background: var(--c-action);
  box-shadow: 0 var(--step-2) 0 var(--c-action-step);
  color: var(--c-text-on-action);
}
.cards-onboarding-popover[aria-busy="true"] .driver-popover-footer-btn {
  pointer-events: none;
  opacity: .55;
}
.cards-onboarding-popover.driver-popover-side-left .driver-popover-arrow {
  border-left-color: var(--c-outline);
}
.cards-onboarding-popover.driver-popover-side-right .driver-popover-arrow {
  border-right-color: var(--c-outline);
}
.cards-onboarding-popover.driver-popover-side-top .driver-popover-arrow {
  border-top-color: var(--c-outline);
}
.cards-onboarding-popover.driver-popover-side-bottom .driver-popover-arrow {
  border-bottom-color: var(--c-outline);
}
.onboarding-card-anatomy {
  position: fixed;
  z-index: 9999;
  inset: 0;
  display: grid;
  place-items: center;
  pointer-events: none;
}
.onboarding-card-anatomy[data-active="false"] {
  visibility: hidden;
}
.onboarding-card-anatomy > .cards-catalog-card {
  width: 215px;
  height: 338px;
  pointer-events: auto;
}
.onboarding-card-anatomy [data-card-region].driver-active-element {
  border-radius: var(--radius-xs);
  outline: var(--stroke-l) solid var(--c-selection);
  outline-offset: var(--space-1);
}
@media (prefers-reduced-motion: reduce) {
  .driver-fade .driver-overlay,
  .driver-fade .driver-popover { animation: none; }
}
/* Assistant (02 §9, 05 §10): a round 40px coin launcher, a panel with a dark
   rim and a step, slate bubbles for the assistant, sunken ones for the player,
   and a gold aim around the element it points at. */
.cards-ai-launcher.cards-kit-button {
  position: fixed;
  z-index: 175;
  right: 16px;
  bottom: calc(var(--cards-beta-ticker-reserve) + 8px);
  width: 40px;
  height: 40px;
  min-height: 0;
  display: grid;
  place-items: center;
  border: var(--stroke-l) solid var(--c-outline);
  border-radius: var(--radius-round);
  padding: 0;
  background: var(--c-panel-raised);
  box-shadow: 0 var(--step-2) 0 var(--c-step-shadow);
  color: var(--c-selection);
  text-decoration: none;
  cursor: pointer;
}
.cards-ai-launcher-label { display: none; }
@media (hover: hover) and (pointer: fine) {
  .cards-ai-launcher.cards-kit-button:hover {
    background: color-mix(in srgb, var(--c-panel-raised) 85%, var(--c-text-on-color));
    color: var(--c-selection);
    text-decoration: none;
    transform: translateY(calc(var(--step-1) * -1));
  }
}
.cards-ai-panel,
.cards-ai-panel * {
  box-sizing: border-box;
}
.cards-ai-panel {
  position: fixed;
  z-index: 175;
  right: 18px;
  bottom: calc(var(--cards-beta-ticker-reserve) + 18px);
  width: min(400px, calc(100vw - 24px));
  height: min(620px, calc(100vh - 24px));
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto auto;
  overflow: hidden;
  --cards-panel-step: var(--step-4);
  color: var(--c-text-on-dark);
  font: var(--type-text-s);
}
.cards-ai-resize {
  position: absolute;
  z-index: 2;
  top: 0;
  left: 0;
  width: 20px;
  height: 20px;
  border: 0;
  background: transparent;
  cursor: nwse-resize;
  touch-action: none;
}
.cards-ai-resize::before {
  content: "";
  position: absolute;
  top: 9px;
  left: 9px;
  width: 7px;
  height: 7px;
  border-top: var(--stroke-m) solid var(--c-panel-edge);
  border-left: var(--stroke-m) solid var(--c-panel-edge);
}
.cards-ai-resize:hover::before,
.cards-ai-resize:focus-visible::before {
  border-color: var(--c-selection);
}
.cards-ai-sheet-handle {
  display: none;
}
.cards-ai-header {
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3);
  border-bottom: var(--stroke-s) solid var(--c-panel-edge);
  background: var(--c-housing-bg);
  cursor: grab;
  touch-action: none;
  user-select: none;
}
.cards-ai-header:active { cursor: grabbing; }
.cards-ai-identity {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: var(--space-2);
}
.cards-ai-mark {
  width: 30px;
  height: 30px;
  flex: none;
  display: grid;
  place-items: center;
  border: var(--stroke-m) solid var(--c-outline);
  border-radius: var(--radius-round);
  background: var(--c-panel-raised);
  color: var(--c-selection);
}
.cards-ai-identity > div {
  min-width: 0;
  display: grid;
  gap: 2px;
}
.cards-ai-identity strong { font: var(--type-display-2xs); text-transform: uppercase; }
.cards-ai-identity span:not(.cards-ai-mark) {
  overflow: hidden;
  color: var(--c-text-muted-on-dark);
  font: var(--type-text-xs);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cards-ai-header-actions {
  flex: none;
  display: flex;
  gap: var(--space-1);
}
.cards-ai-messages {
  min-height: 0;
  overflow: auto;
  overscroll-behavior-y: none;
  touch-action: pan-y;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-3);
  scrollbar-color: var(--c-panel-edge) transparent;
}
.cards-ai-welcome {
  margin: auto 0;
  padding: var(--space-4);
  border: var(--stroke-m) solid var(--c-outline);
  border-radius: var(--radius-m);
  background: var(--c-panel-raised);
}
.cards-ai-welcome > strong { color: var(--c-selection); font: var(--type-display-xs); text-transform: uppercase; }
.cards-ai-welcome > p {
  margin: var(--space-2) 0 var(--space-3);
  color: var(--c-text-muted-on-dark);
  font: var(--type-text-s);
}
.cards-ai-suggestions { display: grid; gap: var(--space-2); }
.cards-ai-suggestions .cards-kit-button { justify-content: flex-start; text-align: left; }
.cards-ai-message {
  width: fit-content;
  max-width: 92%;
  display: grid;
  gap: var(--space-2);
  padding: var(--space-3);
  border: var(--stroke-m) solid var(--c-outline);
  border-radius: var(--radius-m);
  background: var(--c-panel-raised);
}
.cards-ai-message-user {
  align-self: end;
  border-color: var(--c-panel-edge);
  background: var(--c-field-bg);
}
.cards-ai-message-assistant { align-self: start; }
.cards-ai-message-meta {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  color: var(--c-text-muted-on-dark);
  font: var(--type-text-xs);
}
.cards-ai-message-meta span {
  border: var(--stroke-m) solid var(--c-outline);
  border-radius: var(--radius-xs);
  padding: 1px var(--space-1);
  background: var(--c-energy);
  color: var(--c-text-on-energy);
  font: var(--type-display-3xs);
}
.cards-ai-message-copy {
  color: var(--c-text-on-dark);
  font: var(--type-text-m);
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}
.cards-ai-markdown {
  display: grid;
  gap: var(--space-3);
  white-space: normal;
}
.cards-ai-markdown > * {
  margin: 0;
}
.cards-ai-markdown h2,
.cards-ai-markdown h3,
.cards-ai-markdown h4 {
  color: var(--c-selection);
  font: var(--type-display-2xs);
  text-transform: uppercase;
}
.cards-ai-markdown ul,
.cards-ai-markdown ol {
  display: grid;
  gap: var(--space-2);
  padding-left: 21px;
  list-style-position: outside;
}
.cards-ai-markdown li {
  padding-left: 3px;
}
.cards-ai-markdown li::marker {
  color: var(--c-selection);
}
.cards-ai-markdown strong {
  color: var(--c-text-on-dark);
  font-weight: 700;
}
.cards-ai-markdown em {
  color: var(--c-text-muted-on-dark);
}
.cards-ai-markdown strong em {
  color: inherit;
}
.cards-ai-markdown a {
  color: var(--c-selection);
  text-underline-offset: 2px;
}
.cards-ai-markdown code {
  border: var(--stroke-s) solid var(--c-panel-edge);
  border-radius: var(--radius-xs);
  padding: 1px 3px;
  background: var(--c-field-bg);
  color: var(--c-selection);
  font: .9em var(--font-code);
}
.cards-ai-markdown pre {
  max-width: 100%;
  overflow-x: auto;
  border: var(--stroke-s) solid var(--c-panel-edge);
  border-radius: var(--radius-s);
  padding: var(--space-2);
  background: var(--c-field-bg);
  white-space: pre;
}
.cards-ai-markdown pre code {
  border: 0;
  padding: 0;
  background: transparent;
  white-space: inherit;
}
.cards-ai-markdown blockquote {
  border-left: var(--stroke-l) solid var(--c-selection);
  padding-left: var(--space-2);
  color: var(--c-text-muted-on-dark);
}
.cards-ai-inline-action { width: fit-content; }
.cards-ai-deck-proposal {
  display: grid;
  gap: var(--space-2);
  padding-top: var(--space-2);
  border-top: var(--stroke-s) solid var(--c-panel-edge);
}
.cards-ai-deck-proposal > strong { color: var(--c-selection); font: var(--type-display-2xs); text-transform: uppercase; }
.cards-ai-deck-proposal > p {
  margin: 0;
  color: var(--c-text-muted-on-dark);
  font: var(--type-text-xs);
}
.cards-ai-deck-analysis {
  display: grid;
  gap: var(--space-1);
  padding: var(--space-2);
  border: var(--stroke-s) solid var(--c-panel-edge);
  border-radius: var(--radius-s);
  background: var(--c-field-bg);
}
.cards-ai-deck-analysis > strong {
  color: var(--c-text-on-dark);
  font: var(--type-text-xs);
}
.cards-ai-deck-analysis > p,
.cards-ai-deck-stale {
  margin: 0;
  color: var(--c-text-muted-on-dark);
  font: var(--type-text-xs);
  font-weight: 500;
}
.cards-ai-deck-analysis b { color: var(--c-text-on-dark); }
.cards-ai-deck-stale { color: var(--c-selection); }
.cards-ai-deck-proposal ul {
  max-height: 160px;
  overflow: auto;
  display: grid;
  gap: 3px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.cards-ai-deck-proposal li {
  display: flex;
  justify-content: space-between;
  gap: var(--space-2);
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-xs);
  background: var(--c-field-bg);
  color: var(--c-text-on-dark);
  font: var(--type-text-xs);
  font-weight: 500;
}
.cards-ai-deck-proposal li span { min-width: 0; overflow-wrap: anywhere; }
.cards-ai-deck-proposal li b { flex: none; color: var(--c-selection); font: var(--type-display-3xs); }
.cards-ai-thinking {
  align-self: start;
  display: flex;
  align-items: center;
  gap: var(--space-1);
  color: var(--c-text-muted-on-dark);
  font: var(--type-text-xs);
}
/* Three stepped dots, like the waiting Ready button. */
.cards-ai-thinking span {
  width: 4px;
  height: 4px;
  background: var(--c-selection);
  animation: cards-ready-dot var(--dur-loader) var(--steps-flash) infinite;
}
.cards-ai-thinking span:nth-child(2) { animation-delay: calc(var(--dur-loader) / 3); }
.cards-ai-thinking span:nth-child(3) { animation-delay: calc(var(--dur-loader) * 2 / 3); }
.cards-ai-error {
  margin: 0 var(--space-3) var(--space-2);
  padding: var(--space-2) var(--space-3);
  border: var(--stroke-m) solid var(--c-negative);
  border-radius: var(--radius-m);
  background: var(--c-panel-bg);
  color: var(--c-text-negative-on-dark);
  font: var(--type-text-xs);
  font-weight: 500;
}
.cards-ai-composer {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border-top: var(--stroke-s) solid var(--c-panel-edge);
  background: var(--c-housing-bg);
}
/* The composer is a field (02 §2.6): sunken, gold rim on focus. */
.cards-ai-composer textarea {
  width: 100%;
  min-height: 46px;
  max-height: 120px;
  resize: vertical;
  border: var(--stroke-l) solid var(--c-panel-edge);
  border-radius: var(--radius-m);
  padding: var(--space-2);
  background: var(--c-field-bg);
  box-shadow: inset 0 var(--step-2) 0 color-mix(in srgb, var(--c-step-shadow) 35%, transparent);
  color: var(--c-text-on-dark);
  font: var(--type-text-s);
  outline: none;
}
.cards-ai-composer textarea::placeholder { color: var(--c-text-muted-on-dark); }
.cards-ai-composer textarea:focus { border-color: var(--c-selection); }
.cards-ai-send { align-self: stretch; min-width: 72px; }
/* The element the assistant points at gets a static gold aim (outline is not
   animated, 03 §7). */
.cards-ai-highlight {
  position: relative;
  z-index: 174;
  outline: var(--stroke-l) solid var(--c-selection) !important;
  outline-offset: var(--space-1) !important;
}
@media (max-width: 620px) {
  /* Монетка помощника поднята над бегущей строкой BETA: полоса фиксирована по
     низу экрана. Тот же отступ от полосы, что у боевой кнопки «Готов» (8px), —
     они стоят в одной строке, и «Готов» оставляет справа зазор ровно под неё. */
  .cards-ai-launcher.cards-kit-button {
    right: 8px;
    bottom: calc(var(--cards-beta-ticker-reserve) + 8px);
    width: 24px;
    height: 24px;
    border-width: var(--stroke-m);
    box-shadow: 0 var(--step-1) 0 var(--c-step-shadow);
  }
  .cards-ai-launcher svg {
    width: 14px;
    height: 14px;
  }
  .cards-ai-launcher-label { display: none; }
  /* !important на ширине — не украшение: провайдер помнит размер настольного
     окна помощника и печатает его инлайном (width/height в style), а инлайн
     сильнее обычного правила медиазапроса. Без него телефонный шит открывался
     шириной прошлого десктопного окна, те самые «пол-экрана». */
  .cards-ai-panel {
    inset: auto 0 0 !important;
    width: 100% !important;
    height: var(--cards-ai-mobile-height, min(72dvh, 620px)) !important;
    max-height: calc(100dvh - 12px);
    border-right: 0;
    border-bottom: 0;
    border-left: 0;
    border-radius: var(--radius-2xl) var(--radius-2xl) 0 0;
    box-shadow: none;
    transition: height var(--dur-base) var(--ease-out);
    animation: cards-ai-sheet-enter var(--dur-move) var(--ease-out);
    overscroll-behavior: none;
    will-change: height;
  }
  .cards-ai-panel[data-mobile-dragging="true"] {
    transition: none;
    user-select: none;
  }
  /* Хват шита заметно больше нарисованной полоски: пальцем в 24px-строку по
     центру попасть удаётся не всегда. Растёт только область — полоска рисуется
     ::before и остаётся ровно там, где была. */
  .cards-ai-sheet-handle {
    position: absolute;
    z-index: 3;
    top: 0;
    left: 50%;
    width: min(60%, 220px);
    min-width: 76px;
    height: 44px;
    display: block;
    border: 0;
    padding: 0;
    background: transparent;
    transform: translateX(-50%);
    cursor: ns-resize;
    touch-action: none;
  }
  .cards-ai-sheet-handle::before {
    content: "";
    position: absolute;
    top: 7px;
    left: 50%;
    width: 32px;
    height: 4px;
    background: var(--c-text-muted-on-dark);
    transform: translateX(-50%);
  }
  .cards-ai-sheet-handle:hover::before,
  .cards-ai-sheet-handle:focus-visible::before {
    background: var(--c-selection);
  }
  .cards-ai-sheet-handle:focus-visible { outline: none; }
  .cards-ai-header {
    padding-top: 18px;
    cursor: default;
    touch-action: none;
  }
  .cards-ai-composer {
    padding-bottom: max(var(--space-2), env(safe-area-inset-bottom));
  }
  .cards-ai-composer textarea { font: var(--type-text-input); }
  .cards-ai-resize { display: none; }
}
@keyframes cards-ai-sheet-enter {
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
}
@media (prefers-reduced-motion: reduce) {
  .cards-ai-thinking span { animation: none; }
  .cards-ai-panel {
    transition: none;
    animation: none;
  }
  .cards-ai-messages { scroll-behavior: auto; }
}
${SURFACE_CSS}`;

const KIT_STYLE_ID = 'cards-kit-styles';

export function injectKitStyles(doc: Document = document): void {
  const existing = doc.getElementById(KIT_STYLE_ID);
  if (existing !== null) {
    if (existing.tagName !== 'STYLE') {
      throw new Error(`injectKitStyles: #${KIT_STYLE_ID} занят не style-элементом`);
    }
    if (existing.textContent !== KIT_CSS) existing.textContent = KIT_CSS;
    return;
  }
  const style = doc.createElement('style');
  style.id = KIT_STYLE_ID;
  style.textContent = KIT_CSS;
  doc.head.appendChild(style);
}
